import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Alert, MenuItem } from '@mui/material';
import {
  normalizeEmailAddress,
  normalizePhoneNumber,
  sanitizePhoneNumberInput,
  validateOptionalEmailAddress,
  validatePhoneNumber,
} from '../../utils/contactValidation';

const AddRelativeDialog = ({ open, onClose, onAdd, clients }) => {
  const [form, setForm] = useState({
    relative_name: '',
    tel_no: '',
    email: '',
    client: ''
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ tel_no: '', email: '' });
  const [loading, setLoading] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setForm({ relative_name: '', tel_no: '', email: '', client: '' });
      setError('');
      setFieldErrors({ tel_no: '', email: '' });
    }
  }, [open]);

  // Debug logging
  useEffect(() => {
    console.log('Current form state:', form);
    console.log('Available clients:', clients);
  }, [form, clients]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'tel_no'
        ? sanitizePhoneNumberInput(value)
        : name === 'email'
        ? value.trimStart()
        : value;

    setForm(prev => ({ ...prev, [name]: nextValue }));
    if (name === 'tel_no') {
      setFieldErrors((prev) => ({ ...prev, tel_no: nextValue ? validatePhoneNumber(nextValue) : '' }));
    }
    if (name === 'email') {
      setFieldErrors((prev) => ({ ...prev, email: validateOptionalEmailAddress(nextValue) }));
    }
  };
  // const handleChange = (e) => {
  //   const { name, value } = e.target;
    
  //   // Special handling for client selection
  //   if (name === 'client') {
  //     // Ensure we store the client ID as a number
  //     const clientId = value === '' ? '' : parseInt(value, 10);
  //     console.log('Selected client ID:', clientId);
  //     setForm(prev => ({ ...prev, [name]: clientId }));
  //   } else {
  //     setForm(prev => ({ ...prev, [name]: value }));
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const phoneError = validatePhoneNumber(form.tel_no);
    const emailError = validateOptionalEmailAddress(form.email);
    if (phoneError || emailError) {
      setFieldErrors({ tel_no: phoneError, email: emailError });
      setError(phoneError || emailError);
      setLoading(false);
      return;
    }
    
    // Ensure client is a number before submitting
    const submitData = {
      ...form,
      tel_no: normalizePhoneNumber(form.tel_no),
      email: normalizeEmailAddress(form.email),
      client: parseInt(form.client, 10)
    };
    
    console.log('Submitting form data:', submitData);
    
    try {
      await onAdd(submitData);
      setForm({ relative_name: '', tel_no: '', email: '', client: '' });
    } catch (err) {
      setError(err.message || 'Failed to add relative');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Relative</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField 
            label="Relative Name" 
            name="relative_name" 
            value={form.relative_name} 
            onChange={handleChange} 
            fullWidth 
            margin="normal" 
            required 
          />
          <TextField 
            label="Tel No" 
            name="tel_no" 
            value={form.tel_no} 
            onChange={handleChange} 
            fullWidth 
            margin="normal" 
            required 
            type="tel"
            inputProps={{ inputMode: 'tel', maxLength: 20 }}
            error={Boolean(fieldErrors.tel_no)}
            helperText={fieldErrors.tel_no || 'Use 7 to 15 digits. + is allowed.'}
          />
          <TextField
            label="Email Address"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email || 'Optional'}
          />
          <TextField 
            select 
            label="Client" 
            name="client" 
            value={form.client} 
            onChange={handleChange} 
            fullWidth 
            margin="normal" 
            required
            error={form.client === ''}
            helperText={form.client === '' ? 'Please select a client' : ''}
          >
            <MenuItem value="">
              <em>Select a client</em>
            </MenuItem>
            {clients && clients.map((client) => {
              const clientId = client.client_id ?? client.id;  // Use nullish coalescing for safety
              if (!clientId) return null;  // skip if no ID
              
              return (
                <MenuItem key={clientId} value={clientId.toString()}>
                  {client.first_name} {client.last_name}
                </MenuItem>
              );
            })}
          </TextField>
          <DialogActions>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={loading || !form.client}
            >
              {loading ? 'Saving...' : 'Add Relative'}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddRelativeDialog; 
